import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ParentBottomTabBar } from '@/components/ui/ParentBottomTabBar';
import { useAuthStore } from '@/store/authStore';
import { useData } from '@/hooks/useData';
import { apiFetch } from '@/services/apiGateway';
import { GlobalHeader } from '@/components/GlobalHeader';
import i18n from '@/i18n';
import { isTimetableRequired } from '@/utils/academicReset';

// ── Types ──────────────────────────────────────────────────────────────────────

interface Child {
  child_id?: string;
  id?: string;
  display_name?: string;
  name?: string;
  mode?: string;
  date_of_birth?: string;
  streakCount: number;
}

function calcAge(dob: string): number {
  const today = new Date();
  const birth = new Date(dob);
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

interface ChildrenData {
  children: Child[];
}

interface Exam {
  subject: string;
  date: string;
  [key: string]: unknown;
}

interface ExamsData {
  exams: Exam[];
}

interface Proposal {
  subject: string;
  durationMin: number;
  [key: string]: unknown;
}

interface DashboardData {
  proposals: Proposal[];
}

interface TimetableData {
  entries: unknown[];
}

// ── ChildSummaryCard ──────────────────────────────────────────────────────────

function ChildSummaryCard({ child }: { child: Child }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const safeId = child.child_id ?? child.id;

  const [regenLoading, setRegenLoading] = useState(false);
  const [regenCode, setRegenCode] = useState<string | null>(null);
  const [regenExpires, setRegenExpires] = useState<string | null>(null);
  const [regenError, setRegenError] = useState<string | null>(null);

  // The server is the only source of truth for "the current code" — a value
  // cached client-side (e.g. sessionStorage) can silently go stale the
  // moment a second regeneration happens (another click, another tab, a
  // prior visit), and displaying/relaying that stale code fails with a
  // confusing "expired" error even though it was actually just superseded.
  // Re-fetching on every mount means the display always matches the AUTH
  // record's real current code_hash.
  const joinCodeEndpoint =
    child.mode === 'high_school' && safeId ? `/children/${safeId}/join-code` : null;
  const { data: currentCode } = useData<{ join_code: string; join_code_expires_at: string }>(
    joinCodeEndpoint,
  );

  const code = regenCode ?? currentCode?.join_code ?? null;
  const expires = regenExpires ?? currentCode?.join_code_expires_at ?? null;
  const isExpired = expires ? new Date(expires) < new Date() : true;
  const displayCode = isExpired ? null : code;

  const handleRegen = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setRegenLoading(true);
    setRegenError(null);
    try {
      const res = await apiFetch<{ join_code: string; join_code_expires_at: string }>(
        `/children/${safeId}/regenerate-code`,
        { method: 'POST' },
      );
      setRegenCode(res.join_code);
      setRegenExpires(res.join_code_expires_at);
    } catch {
      setRegenError(t('parent_dash.regen_failed'));
    } finally {
      setRegenLoading(false);
    }
  };

  const { data: dashboardData } = useData<DashboardData>(
    safeId ? `/children/${safeId}/proposals` : null,
  );
  const { data: examsData } = useData<ExamsData>(
    safeId ? `/children/${safeId}/exams` : null,
  );
  const { data: timetableData, isLoading: timetableLoading } = useData<TimetableData>(
    safeId ? `/children/${safeId}/timetable` : null,
  );

  // US-PRF-02: the localStorage flag alone isn't reliable across devices —
  // a parent's browser has no way to learn that the child cleared it on
  // their own device by actually uploading a new timetable. Cross-checking
  // against the real backend timetable data (same pattern as
  // StudentDashboard's isTimetableEmpty) means the banner disappears the
  // moment a timetable genuinely exists, regardless of which device set or
  // cleared the flag.
  const isTimetableEmpty =
    !timetableLoading &&
    (!timetableData || !timetableData.entries || timetableData.entries.length === 0);
  const academicResetPending = isTimetableEmpty && isTimetableRequired(safeId);

  const today = new Date().toISOString().slice(0, 10);
  const nextExam = (examsData?.exams ?? [])
    .filter((e) => e.date >= today)
    .sort((a, b) => a.date.localeCompare(b.date))[0] ?? null;

  const latestProposal = dashboardData?.proposals?.[0] ?? null;

  const displayName = child.display_name ?? child.name ?? t('parent_dash.student_fallback');

  return (
    <div
      className="card"
      onClick={() => navigate(`/parent/child/${safeId}`)}
      style={{ cursor: 'pointer', marginBottom: '16px' }}
    >
      <div style={{ marginBottom: '10px' }}>
        <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: 'var(--text-dark)' }}>
          {displayName}
        </h3>
        {child.date_of_birth && (
          <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
            {t('parent_student.age_label', { age: calcAge(child.date_of_birth) })}
          </p>
        )}
      </div>

      {/* ── High-priority: academic year reset, timetable still missing (US-PRF-02) ── */}
      {safeId && academicResetPending && (
        <div
          style={{
            background: '#FEF2F2',
            border: '1px solid var(--danger)',
            borderRadius: '8px',
            padding: '10px 12px',
            marginBottom: '10px',
          }}
        >
          <p style={{ margin: '0 0 8px', fontSize: '0.82rem', color: 'var(--danger)', fontWeight: 600 }}>
            ⚠️ {t('academic_context.banner_title')}
          </p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/parent/child/${safeId}/timetable/scan`);
            }}
            style={{ width: '100%', fontSize: '0.85rem', padding: '8px 12px' }}
          >
            📷 {t('parent_student.upload_schedule')}
          </button>
        </div>
      )}

      {nextExam && (
        <div style={{
          borderLeft: '3px solid var(--danger)',
          paddingLeft: '10px',
          marginBottom: '8px',
          fontSize: '0.82rem',
          color: 'var(--text-dark)',
        }}>
          📅 {t('parent_dash.next_exam')}: <strong>{nextExam.subject}</strong> — {nextExam.date}
        </div>
      )}

      {latestProposal && (
        <p style={{ margin: 0, fontSize: '0.82rem', color: 'var(--text-muted)' }}>
          🤖 {t('parent_dash.ai_proposal')}: {latestProposal.durationMin}min {latestProposal.subject}
        </p>
      )}

      {!nextExam && !latestProposal && (
        <p style={{ margin: 0, fontSize: '0.82rem', color: 'var(--text-muted)' }}>
          {t('parent_dash.empty')}
        </p>
      )}

      {child.mode === 'high_school' && (
        <div
          style={{ marginTop: '12px', borderTop: '1px solid var(--border)', paddingTop: '12px' }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="btn btn-outline"
            onClick={handleRegen}
            disabled={regenLoading}
            style={{ width: '100%', fontSize: '0.85rem', padding: '8px 12px' }}
          >
            🔄 {regenLoading ? '…' : t('parent_dash.regen_code')}
          </button>

          {displayCode && (
            <div style={{
              marginTop: '10px',
              background: '#F0FDF4',
              border: '1px solid var(--success)',
              borderRadius: '8px',
              padding: '10px 12px',
              fontSize: '0.85rem',
            }}>
              <span style={{ color: 'var(--text-muted)', marginRight: '6px' }}>
                {t('parent_dash.regen_new_code')}:
              </span>
              <strong style={{ fontFamily: 'monospace', letterSpacing: '2px', color: 'var(--text-dark)' }}>
                {displayCode.slice(0, 4)}-{displayCode.slice(4)}
              </strong>
            </div>
          )}

          {regenError && (
            <p style={{ margin: '8px 0 0', fontSize: '0.8rem', color: 'var(--danger)' }}>
              {regenError}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function linkErrorMessage(err: unknown): string {
  const msg = err instanceof Error ? err.message : '';
  const t = i18n.t.bind(i18n);
  if (msg.includes('410')) return t('parent_dash.link_err_expired');
  if (msg.includes('404')) return t('parent_dash.link_err_invalid');
  return t('parent_dash.link_err_generic');
}

// ── ParentDashboard ───────────────────────────────────────────────────────────

export function ParentDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { sub } = useAuthStore();

  const [refreshKey, setRefreshKey] = useState(0);
  const [inviteCode, setInviteCode] = useState('');
  const [linkError, setLinkError] = useState<string | null>(null);
  const [linkLoading, setLinkLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const endpoint = sub ? `/parents/me/children?r=${refreshKey}` : null;
  const { data, isLoading } = useData<ChildrenData>(endpoint);

  const children = data?.children ?? [];

  const handleLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setLinkError(null);
    setSuccessMsg(null);
    setLinkLoading(true);
    try {
      await apiFetch('/parents/me/link-student', {
        method: 'POST',
        body: JSON.stringify({ invite_code: inviteCode.replace(/\s/g, '') }),
      });
      setInviteCode('');
      setSuccessMsg(t('parent_dash.link_success'));
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setLinkError(linkErrorMessage(err));
    } finally {
      setLinkLoading(false);
    }
  };

  return (
    <div className="page-center">
      <div className="phone-mockup">
        <GlobalHeader />
        <div className="app-content">

          <h2 style={{ margin: '0 0 16px', fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-dark)' }}>
            {t('parent_dash.title')}
          </h2>

          {/* Loading */}
          {isLoading && (
            <div className="loading-skeleton" style={{ height: '80px', marginBottom: '16px' }} />
          )}

          {/* Children list */}
          {!isLoading && children.length > 0 && children.map((child) => (
            <ChildSummaryCard key={child.child_id ?? child.id} child={child} />
          ))}

          {/* Empty state */}
          {!isLoading && children.length === 0 && (
            <div className="card" style={{ textAlign: 'center', marginBottom: '8px' }}>
              <div style={{ fontSize: '2rem', marginBottom: '8px' }}>👨‍👩‍👧</div>
              <p style={{ color: 'var(--text-muted)', margin: 0, fontSize: '0.9rem' }}>
                {t('parent_dash.no_students_linked')}
              </p>
            </div>
          )}

          {/* Add HS student */}
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => navigate('/parent/add-child')}
            style={{ width: '100%', marginBottom: '16px' }}
          >
            + {t('parent_dash.add_hs')}
          </button>

          {/* Divider */}
          <hr className="divider" style={{ margin: '0 0 16px' }} />

          {/* Link a student */}
          <h3 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-dark)', margin: '0 0 12px' }}>
            {t('parent_dash.link_student')}
          </h3>

          {successMsg && (
            <p style={{
              color: 'var(--success)', fontSize: '0.85rem', margin: '0 0 12px',
              padding: '10px 14px', background: '#F0FDF4', borderRadius: '8px',
            }}>
              ✅ {successMsg}
            </p>
          )}

          <form onSubmit={handleLink} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div className="form-group">
              <label htmlFor="invite-code">{t('parent_dash.invite_code')}</label>
              <input
                id="invite-code"
                type="text"
                className={`input-code${linkError ? ' input--error' : ''}`}
                placeholder="XXXX XXXX"
                maxLength={9}
                value={inviteCode}
                onChange={(e) => {
                  setInviteCode(e.target.value.toUpperCase());
                  setLinkError(null);
                  setSuccessMsg(null);
                }}
                disabled={linkLoading}
              />
              {linkError && <p className="input-error-msg">{linkError}</p>}
            </div>

            <button
              type="submit"
              className="btn btn-primary"
              disabled={linkLoading || inviteCode.trim().length < 8}
            >
              {linkLoading ? '…' : t('parent_dash.link_account')}
            </button>
          </form>

        </div>

        <ParentBottomTabBar activeTab="Family" />
      </div>
    </div>
  );
}
